import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import api from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, CheckCircle2, Clock, XCircle, FileX, Upload, ArrowRight, ShieldCheck } from "lucide-react";

const LIGHT_DOT = {
  green:  { bg: "bg-emerald-500",  ring: "ring-emerald-500/30",  text: "text-emerald-600",  label: "On track" },
  yellow: { bg: "bg-amber-400",    ring: "ring-amber-400/30",    text: "text-amber-600",    label: "Action pending" },
  red:    { bg: "bg-rose-500",     ring: "ring-rose-500/30",     text: "text-rose-600",     label: "Blocked" },
};

const DOC_STATE = {
  approved: { Icon: CheckCircle2, color: "text-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" },
  pending:  { Icon: Clock,        color: "text-amber-500",   chip: "bg-amber-50 text-amber-700 border-amber-200",       label: "Under review" },
  rejected: { Icon: XCircle,      color: "text-rose-500",    chip: "bg-rose-50 text-rose-700 border-rose-200",          label: "Rejected" },
  missing:  { Icon: FileX,        color: "text-slate-400",   chip: "bg-slate-50 text-slate-500 border-slate-200",       label: "Not uploaded" },
};

/**
 * Unified Compliance Readiness widget — the same component used on
 * the user-side (KYC page + Client Dashboard) so the user always sees
 * exactly where they are in the pipeline.
 *
 * `hideNextAction` (STARSEED-CAPMARKETS-257): the dashboard passes it while
 * any onboarding-checklist item is open, because the checklist owns the
 * call to action there and this widget's document-pipeline next action can
 * name the same step with a different destination. The stepper and the
 * document list are unaffected.
 */
const ComplianceReadiness = ({ compact = false, onUploadClick = null, hideNextAction = false, refreshToken = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // STARSEED-CAPMARKETS-250 — `refreshToken` exists because this widget holds
  // its own copy of server truth. It renders the checklist from
  // `/compliance/me`, while the page that owns the upload renders the slot rows
  // from `/kyc/user/{id}`. Two components, two fetches, one underlying fact —
  // and until now only one of them was refreshed after an upload, so the
  // member watched the slot row fill in while the checklist above it still
  // read "Not uploaded" until a full page reload.
  //
  // Deliberately a token rather than a `data` prop: the two endpoints return
  // different shapes (this one carries stages and next_action, which the page
  // has no use for), so lifting the fetch would push compliance-stage state
  // into a page about documents. The token says only "the server moved" and
  // leaves each surface owning its own read.
  //
  // `loading` is never set back to true: a refetch keeps the rendered
  // checklist on screen and swaps it when the response lands, instead of
  // collapsing the widget to a skeleton on every upload.
  //
  // The two failure paths are NOT the same, which is why the catch branches.
  // On the mount fetch, failing leaves `data` null and the widget renders
  // nothing — absent, which reads as absent. On a REFETCH, failing leaves the
  // PREVIOUS payload on screen, and the previous payload is the one that says
  // "Not uploaded" about the document the member just uploaded. Silence there
  // reproduces this ticket's exact symptom through the error path, with the
  // slot row below already showing the file name. So a failed refresh has to
  // say so; only the member can act on it, by reloading.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/compliance/me");
        if (!cancelled) setData(r.data);
      } catch (_) {
        // Mount: silent — the caller decides UX for the unauth state.
        if (!cancelled && refreshToken > 0) {
          toast.error("Your document status could not be refreshed — reload the page to see the latest.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshToken]);

  if (loading) {
    return <div className="h-32 rounded-lg bg-slate-100 animate-pulse" data-testid="compliance-readiness-loading" />;
  }
  if (!data) return null;

  const { stages, kyc_docs, next_action } = data;

  // Resolve the doc type for the "next_action" CTA so it triggers the file picker
  // for the specific missing/rejected document when we're on the KYC page.
  const nextActionDocType =
    next_action && next_action.stage === "kyc"
      ? (kyc_docs.find((d) => d.required && (d.state === "missing" || d.state === "rejected")) || {}).type
      : null;
  const renderNextActionAsUpload = !!(onUploadClick && nextActionDocType);

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="compliance-readiness">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-blue-600" />
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Your Compliance Readiness
            </p>
          </div>
          {next_action && !hideNextAction && (
            (() => {
              // localdev-readability-001: when next_action represents a
              // passive STATUS (e.g. "Awaiting admin review of your KYC
              // docs") rather than an actionable CTA, render as a
              // readable status pill instead of a saturated blue button.
              // The investor feedback called out the blue button as
              // "hard to read" — long text + small font on a vibrant
              // background tested as fatiguing even at AAA contrast.
              //
              // Heuristic: the `passive` flag from the API marks
              // statuses that the user can't act on (under review,
              // pending approval, etc.). Falls back to checking the
              // label string for "Awaiting" / "Pending" / "Under review"
              // for back-compat with backends that don't yet return the
              // flag.
              const labelStr = String(next_action.label || "");
              const isPassive =
                next_action.passive === true ||
                /^\s*(awaiting|pending|under review|in review|reviewing)/i.test(labelStr);
              if (isPassive) {
                return (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50 text-blue-900 border border-blue-200 text-sm font-semibold"
                    data-testid="compliance-next-action-status"
                  >
                    <Clock size={14} className="text-blue-600 shrink-0" />
                    {next_action.label}
                  </span>
                );
              }
              return renderNextActionAsUpload ? (
                <Button
                  size="sm"
                  onClick={() => onUploadClick(nextActionDocType)}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-sm font-semibold"
                  data-testid="compliance-next-action"
                >
                  {next_action.label} <ArrowRight size={14} className="ml-1.5" />
                </Button>
              ) : (
                <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-sm font-semibold" data-testid="compliance-next-action">
                  <Link to={next_action.href}>
                    {next_action.label} <ArrowRight size={14} className="ml-1.5" />
                  </Link>
                </Button>
              );
            })()
          )}
        </div>

        {/* Horizontal stepper — 4 traffic lights */}
        <div className="flex items-stretch gap-2 sm:gap-3 overflow-x-auto pb-1" data-testid="compliance-stepper">
          {stages.map((s, idx) => {
            const dot = LIGHT_DOT[s.light] || LIGHT_DOT.red;
            return (
              <div key={s.key} className="flex items-stretch gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="flex-1 min-w-[140px] rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`stage-${s.key}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`relative inline-flex h-3 w-3 rounded-full ${dot.bg} ring-4 ${dot.ring}`} data-testid={`stage-${s.key}-light`}>
                      {s.light !== "green" && (
                        <span className={`absolute inline-flex h-full w-full rounded-full ${dot.bg} opacity-40 animate-ping`} />
                      )}
                    </span>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">0{idx + 1}</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 leading-tight">{s.label}</p>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">{s.summary}</p>
                </div>
                {idx < stages.length - 1 && (
                  <ChevronRight size={14} className="text-slate-300 self-center shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* Document checklist (skip in compact mode) */}
        {!compact && (
          <div className="mt-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
              Document checklist
            </p>
            <ul className="space-y-2" data-testid="compliance-doc-checklist">
              {kyc_docs.map((d) => {
                const ds = DOC_STATE[d.state] || DOC_STATE.missing;
                return (
                  <li key={d.type} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`doc-${d.type}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <ds.Icon className={`${ds.color} shrink-0`} size={18} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {d.label}
                          {d.required && <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-600 font-semibold">required</span>}
                          {!d.required && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">optional</span>}
                        </p>
                        {d.uploaded_at && (
                          <p className="text-[11px] text-slate-500 truncate">
                            {d.file_name} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`${ds.chip} border text-[11px] font-medium`}>{ds.label}</Badge>
                      {(d.state === "missing" || d.state === "rejected") && (
                        onUploadClick ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUploadClick(d.type)}
                            className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`doc-upload-${d.type}`}
                          >
                            <Upload size={12} className="mr-1" />
                            {d.state === "rejected" ? "Re-upload" : "Upload"}
                          </Button>
                        ) : (
                          <Button asChild variant="ghost" size="sm" className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            <Link to="/kyc" data-testid={`doc-upload-link-${d.type}`}>
                              <Upload size={12} className="mr-1" />
                              {d.state === "rejected" ? "Re-upload" : "Upload"}
                            </Link>
                          </Button>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplianceReadiness;
